import { useCallback, useEffect, useState } from "react";
import {
  Loader2, Send, StickyNote, CheckCircle2, RotateCcw, Film, Clock, Mail, User,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { AttachmentLinks } from "./AttachmentPicker";
import {
  STATUS_STYLE, SEVERITY_STYLE, STATUS_LABEL, fmtDate, fmtDuration, fmtClock,
} from "../../lib/support";

const STATUSES = ["new", "investigating", "waiting_for_user", "resolved", "reopened"];
const SEVERITIES = ["minor", "incorrect", "major", "critical"];

const Field = ({ label, value, testId, mono = false }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
    <div
      className={`mt-1 text-sm text-white break-words ${mono ? "font-mono text-xs" : ""}`}
      data-testid={testId}
    >
      {value || "—"}
    </div>
  </div>
);

export const AdminTicketDialog = ({ ticketId, open, onOpenChange, onChanged }) => {
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  const [replyStatus, setReplyStatus] = useState("waiting_for_user");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/support/tickets/${ticketId}`);
      setT(data);
    } catch {
      toast.error("Could not load ticket");
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = (status) =>
    act(() => api.post(`/admin/support/tickets/${ticketId}/status`, { status }),
      `Status → ${STATUS_LABEL[status]}`);

  const setSeverity = (severity) =>
    act(() => api.post(`/admin/support/tickets/${ticketId}/severity`, { severity }),
      `Internal severity → ${severity}`);

  const sendReply = () => {
    if (reply.trim().length < 1) return toast.error("Write a reply first");
    return act(async () => {
      await api.post(`/admin/support/tickets/${ticketId}/reply`, {
        body: reply.trim(),
        set_status: replyStatus,
      });
      setReply("");
    }, "Reply sent to the customer");
  };

  const addNote = () => {
    if (note.trim().length < 1) return toast.error("Write a note first");
    return act(async () => {
      await api.post(`/admin/support/tickets/${ticketId}/notes`, { body: note.trim() });
      setNote("");
    }, "Internal note added");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="admin-ticket-dialog"
        className="bg-zinc-950 border-zinc-800 text-white rounded-none max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight flex flex-wrap items-center gap-3">
            <span className="font-mono text-sm" data-testid="admin-dialog-ticket-id">{ticketId}</span>
            {t && (
              <>
                <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${STATUS_STYLE[t.status] || ""}`}>
                  {t.status_label}
                </span>
                <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${SEVERITY_STYLE[t.problem.severity] || ""}`}>
                  internal · {t.problem.severity}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-widest border border-zinc-700 text-zinc-400 px-2 py-0.5">
                  reported · {t.problem.reported_severity}
                </span>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading || !t ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
          </div>
        ) : (
          <div className="space-y-6">
            <section className="border border-zinc-800 bg-zinc-900/40 p-5">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-4">
                <User className="w-3 h-3" /> Customer information
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Field label="Email" value={t.customer.email} testId="admin-ticket-customer-email" />
                <Field label="Account id" value={t.customer.account_id} testId="admin-ticket-account-id" mono />
                <Field label="Plan" value={t.customer.plan?.name || t.customer.plan?.key} testId="admin-ticket-plan" />
              </div>
            </section>

            <section className="border border-zinc-800 bg-zinc-900/40 p-5 space-y-5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Problem</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Field label="Category" value={t.problem.category_label} testId="admin-ticket-category" />
                <Field label="Reported severity" value={t.problem.reported_severity} testId="admin-ticket-reported-severity" />
                <Field label="Opened" value={fmtDate(t.created_at)} />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Description</div>
                <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap" data-testid="admin-ticket-description">
                  {t.problem.description}
                </p>
              </div>
              {t.problem.expected_result && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Expected result</div>
                  <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
                    {t.problem.expected_result}
                  </p>
                </div>
              )}
              {t.problem.attachments?.length > 0 && (
                <AttachmentLinks items={t.problem.attachments} testId="admin-ticket-attachment" />
              )}
              {t.problem.cue && (
                <div className="border-l-2 border-blue-500/50 pl-4 space-y-1" data-testid="admin-ticket-cue">
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-blue-300">
                    <Clock className="w-3 h-3" />
                    Line #{t.problem.cue.cue_index} · {fmtClock(t.problem.cue.start_seconds)} → {fmtClock(t.problem.cue.end_seconds)}
                    {t.problem.cue.asr_id && <span className="text-zinc-600">· {t.problem.cue.asr_id}</span>}
                  </div>
                  <div className="text-xs font-mono text-zinc-500">{t.problem.cue.source_text}</div>
                  <div className="text-sm text-white">{t.problem.cue.generated_text}</div>
                </div>
              )}
            </section>

            <section className="border border-zinc-800 bg-zinc-900/40 p-5">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-4">
                <Film className="w-3 h-3" /> Video context
              </div>
              {t.video ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="admin-ticket-video">
                  <Field label="Job id" value={t.video.job_id} mono testId="admin-ticket-job-id" />
                  <Field label="Video name" value={t.video.video_name} />
                  <Field label="Duration" value={fmtDuration(t.video.duration_seconds)} />
                  <Field label="Source language" value={t.video.source_language} />
                  <Field label="Target language" value={t.video.target_language} />
                  <Field label="Processing status" value={t.video.job_status} />
                </div>
              ) : (
                <div className="text-sm text-zinc-500" data-testid="admin-ticket-video-none">
                  Not linked to a video.
                </div>
              )}
            </section>

            <section className="border border-zinc-800 bg-zinc-900/40 p-5 space-y-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                Support actions
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mb-2">Status</div>
                  <Select value={t.status} onValueChange={setStatus} disabled={busy}>
                    <SelectTrigger
                      data-testid="admin-ticket-status-select"
                      className="bg-zinc-950 border-zinc-800 rounded-none h-10 text-white"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`admin-status-opt-${s}`}>
                          {STATUS_LABEL[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mb-2">
                    Internal severity
                  </div>
                  <Select value={t.problem.severity} onValueChange={setSeverity} disabled={busy}>
                    <SelectTrigger
                      data-testid="admin-ticket-severity-select"
                      className="bg-zinc-950 border-zinc-800 rounded-none h-10 text-white"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
                      {SEVERITIES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`admin-severity-opt-${s}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setStatus("resolved")}
                  disabled={busy || t.status === "resolved"}
                  data-testid="admin-resolve-btn"
                  className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest bg-emerald-500 hover:bg-emerald-400 text-zinc-950 disabled:opacity-40 px-4 py-2 transition-colors"
                >
                  <CheckCircle2 className="w-3 h-3" /> Resolve
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("reopened")}
                  disabled={busy || t.status === "reopened"}
                  data-testid="admin-reopen-btn"
                  className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:bg-zinc-900 text-zinc-300 disabled:opacity-40 px-4 py-2 transition-colors"
                >
                  <RotateCcw className="w-3 h-3" /> Reopen
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 pt-2">
                <div className="space-y-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-blue-300">
                    Reply to customer · emailed
                  </div>
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={4}
                    data-testid="admin-reply-input"
                    placeholder="This is visible to the customer."
                    className="bg-zinc-950 border-zinc-800 rounded-none text-white placeholder:text-zinc-600 resize-none"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={replyStatus} onValueChange={setReplyStatus}>
                      <SelectTrigger
                        data-testid="admin-reply-status-select"
                        className="bg-zinc-950 border-zinc-800 rounded-none h-9 w-48 text-white text-xs"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>Then set: {STATUS_LABEL[s]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={sendReply}
                      disabled={busy}
                      data-testid="admin-reply-send-btn"
                      className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-4 py-2 transition-colors"
                    >
                      <Send className="w-3 h-3" /> Send reply
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300">
                    Internal note · never shown to the customer
                  </div>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={4}
                    data-testid="admin-note-input"
                    placeholder="Findings, root cause, what you checked."
                    className="bg-zinc-950 border-amber-500/20 rounded-none text-white placeholder:text-zinc-600 resize-none"
                  />
                  <button
                    type="button"
                    onClick={addNote}
                    disabled={busy}
                    data-testid="admin-note-add-btn"
                    className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 px-4 py-2 transition-colors"
                  >
                    <StickyNote className="w-3 h-3" /> Add internal note
                  </button>
                </div>
              </div>
            </section>

            <section className="space-y-3" data-testid="admin-ticket-thread">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                Thread ({t.messages.length})
              </div>
              {t.messages.length === 0 && (
                <div className="text-sm text-zinc-600">No replies or notes yet.</div>
              )}
              {t.messages.map((m) => (
                <div
                  key={m.message_id}
                  data-testid={`admin-thread-${m.visibility}-${m.message_id}`}
                  className={`border p-4 ${
                    m.visibility === "internal"
                      ? "border-amber-500/30 bg-amber-500/5"
                      : m.author === "support"
                        ? "border-blue-500/30 bg-blue-500/5"
                        : "border-zinc-800 bg-zinc-900/40"
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-300">
                      {m.author === "internal" ? "Internal note" : m.author === "support" ? "Support reply" : "Customer"}
                    </span>
                    {m.visibility === "internal" && (
                      <span className="text-[10px] font-mono uppercase tracking-widest border border-amber-500/40 text-amber-300 px-1.5 py-0.5">
                        not visible to customer
                      </span>
                    )}
                    <span className="text-[10px] font-mono text-zinc-600 ml-auto">{fmtDate(m.created_at)}</span>
                  </div>
                  <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{m.body}</p>
                  {m.attachments?.length > 0 && (
                    <div className="mt-3">
                      <AttachmentLinks items={m.attachments} testId="admin-msg-attachment" />
                    </div>
                  )}
                </div>
              ))}
            </section>

            <section className="border border-zinc-800 bg-zinc-900/40 p-5" data-testid="admin-ticket-notifications">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
                <Mail className="w-3 h-3" /> Notifications sent ({t.notifications.length})
              </div>
              {t.notifications.length === 0 ? (
                <div className="text-sm text-zinc-600">None yet.</div>
              ) : (
                <ul className="space-y-1.5 font-mono text-[11px]">
                  {t.notifications.map((n) => (
                    <li key={`${n.kind}-${n.to}-${n.created_at}`} className="flex flex-wrap gap-x-4 text-zinc-400">
                      <span className="text-zinc-300">{n.kind}</span>
                      <span className="text-zinc-500">{n.to}</span>
                      <span className={n.sent ? "text-emerald-400" : "text-zinc-600"}>
                        {n.sent ? "sent" : "not delivered"}
                      </span>
                      <span className="text-zinc-600 ml-auto">{fmtDate(n.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="text-[10px] font-mono text-zinc-600 mt-3">
                Each notification is claimed once — retries and restarts cannot re-send it.
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
